import inventoryText from "../inventoryI18n.js";
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import AuthContext from "../../context/AuthContext";
import { API_URL } from "../../components/api";

function WarehouseInventoryModal({
  show,
  warehouse,
  onHide,
}) {
  const { user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [transferProduct, setTransferProduct] = useState(null);
  const [toWarehouse, setToWarehouse] = useState("");
  const [transferQuantity, setTransferQuantity] = useState(1);
  const [transferReason, setTransferReason] = useState("");
  const [transferError, setTransferError] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [adjustmentProduct, setAdjustmentProduct] = useState(null);
  const [adjustmentOperation, setAdjustmentOperation] = useState("increase");
  const [adjustmentQuantity, setAdjustmentQuantity] = useState(1);
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [adjustmentError, setAdjustmentError] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [minProduct, setMinProduct] = useState(null);
  const [minQuantity, setMinQuantity] = useState(0);
  const [minError, setMinError] = useState("");
  const [savingMin, setSavingMin] = useState(false);
  const [showRecords, setShowRecords] = useState(false);
  const [recordsProduct, setRecordsProduct] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState("");

  const getHeaders = useCallback(() => {
    const tokens = JSON.parse(
      localStorage.getItem("authTokens") || "{}"
    );
    return {
      Authorization: `Bearer ${tokens.access}`,
    };
  }, []);

  const requestMessage = (requestError, fallback) => (
    requestError.response?.data?.message || fallback
  );

  const loadProducts = useCallback(async (clear = true) => {
    if (!warehouse?.warehouse_id) {
      return;
    }

    setLoading(true);
    setError("");

    if (clear) {
      setProducts([]);
    }

    try {
      const response = await axios.get(
        `${API_URL}/inventory/warehouses/products/`,
        {
          headers: getHeaders(),
          params: {
            username: user.username,
            warehouse_id: warehouse.warehouse_id,
          },
        }
      );

      const rows = Array.isArray(response.data)
        ? response.data
        : [];

      setProducts(
        rows
          .sort((left, right) =>
            String(left.name || "").localeCompare(
              String(right.name || "")
            )
          )
      );
    } catch (requestError) {
      console.error(
        "Unable to load warehouse products:",
        requestError
      );
      setError(
        requestMessage(
          requestError,
          "Warehouse products could not be loaded."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [
    getHeaders,
    user.username,
    warehouse?.warehouse_id,
  ]);

  useEffect(() => {
    if (!show || !warehouse?.warehouse_id) {
      return undefined;
    }

    let active = true;
    setNotice("");
    loadProducts();

    const loadWarehouses = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/inventory/warehouses/`,
          {
            headers: getHeaders(),
            params: {
              username: user.username,
            },
          }
        );

        if (active) {
          setWarehouses(
            Array.isArray(response.data)
              ? response.data
              : []
          );
        }
      } catch (requestError) {
        console.error(
          "Unable to load destination warehouses:",
          requestError
        );

        if (active) {
          setWarehouses([]);
        }
      }
    };

    loadWarehouses();

    return () => {
      active = false;
    };
  }, [
    getHeaders,
    loadProducts,
    show,
    user.username,
    warehouse?.warehouse_id,
  ]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setNotice("");
    }, 3000);

    return () => clearTimeout(timeout);
  }, [notice]);

  const openTransfer = (product) => {
    setTransferProduct(product);
    setToWarehouse("");
    setTransferQuantity(1);
    setTransferReason("");
    setTransferError("");
  };

  const closeTransfer = () => {
    if (!transferring) {
      setTransferProduct(null);
      setTransferError("");
    }
  };

  const submitTransfer = async (event) => {
    event.preventDefault();
    const quantity = Number(transferQuantity);

    if (
      !transferProduct
      || !toWarehouse
      || !Number.isInteger(quantity)
      || quantity < 1
      || quantity > Number(transferProduct.quantity)
      || !transferReason.trim()
    ) {
      setTransferError(
        "Choose a destination, enter a valid quantity, "
        + "and provide a reason."
      );
      return;
    }

    setTransferring(true);
    setTransferError("");

    try {
      await axios.post(
        `${API_URL}/inventory/transfer/`,
        {
          from_warehouse_id: warehouse.warehouse_id,
          to_warehouse_id: toWarehouse,
          products: [
            {
              product_id: transferProduct.product_id,
              quantity,
            },
          ],
          userdealer: user.username,
          reason: transferReason.trim(),
        },
        {
          headers: getHeaders(),
        }
      );

      setNotice(
        `${quantity} ${transferProduct.name} transferred successfully.`
      );
      setTransferProduct(null);
      await loadProducts(false);
    } catch (requestError) {
      console.error(
        "Unable to transfer product:",
        requestError
      );
      setTransferError(
        requestMessage(
          requestError,
          "The product could not be transferred."
        )
      );
    } finally {
      setTransferring(false);
    }
  };

  const openAdjustment = (product) => {
    setAdjustmentProduct(product);
    setAdjustmentOperation("increase");
    setAdjustmentQuantity(1);
    setAdjustmentReason("");
    setAdjustmentError("");
  };

  const closeAdjustment = () => {
    if (!adjusting) {
      setAdjustmentProduct(null);
      setAdjustmentError("");
    }
  };

  const submitAdjustment = async (event) => {
    event.preventDefault();

    const quantity = Number(adjustmentQuantity);
    const currentQuantity = Number(
      adjustmentProduct?.quantity || 0
    );

    if (
      !adjustmentProduct
      || !["increase", "decrease"].includes(
        adjustmentOperation
      )
      || !Number.isInteger(quantity)
      || quantity < 1
      || !adjustmentReason.trim()
    ) {
      setAdjustmentError(
        "Choose Increase or Decrease, enter a positive "
        + "whole-number amount, and provide a reason."
      );
      return;
    }

    if (
      adjustmentOperation === "decrease"
      && quantity > currentQuantity
    ) {
      setAdjustmentError(
        `Only ${currentQuantity} unit(s) are available.`
      );
      return;
    }

    setAdjusting(true);
    setAdjustmentError("");

    try {
      const response = await axios.post(
        `${API_URL}/inventory/warehouses/adjust-quantity/`,
        {
          warehouse_id: warehouse.warehouse_id,
          product_id: adjustmentProduct.product_id,
          operation: adjustmentOperation,
          quantity,
          reason: adjustmentReason.trim(),
        },
        {
          headers: getHeaders(),
        }
      );

      const record = response.data?.record;

      setNotice(
        record
          ? (
              `${adjustmentProduct.name} quantity changed `
              + `from ${record.quantity_before} `
              + `to ${record.quantity_after}.`
            )
          : "Inventory quantity adjusted successfully."
      );

      setAdjustmentProduct(null);
      await loadProducts(false);
    } catch (requestError) {
      console.error(
        "Unable to adjust inventory quantity:",
        requestError
      );

      setAdjustmentError(
        requestMessage(
          requestError,
          "The inventory quantity could not be adjusted."
        )
      );
    } finally {
      setAdjusting(false);
    }
  };

  const openMin = (product) => {
    setMinProduct(product);
    setMinQuantity(Number(product?.min_quantity || 0));
    setMinError("");
  };

  const closeMin = () => {
    if (!savingMin) {
      setMinProduct(null);
      setMinError("");
    }
  };

  const submitMin = async (event) => {
    event.preventDefault();

    const value = Number(minQuantity);

    if (!minProduct || !Number.isInteger(value) || value < 0) {
      setMinError(inventoryText("inventory.MinQuantityInvalid"));
      return;
    }

    setSavingMin(true);
    setMinError("");

    try {
      await axios.post(
        `${API_URL}/inventory/warehouses/min-quantity/`,
        {
          warehouse_id: warehouse.warehouse_id,
          product_id: minProduct.product_id,
          min_quantity: value,
        },
        {
          headers: getHeaders(),
        }
      );

      setNotice(
        `${minProduct.name} `
        + `${inventoryText("inventory.MinQuantitySaved")} ${value}.`
      );

      setMinProduct(null);
      await loadProducts(false);
    } catch (requestError) {
      console.error(
        "Unable to save minimum quantity:",
        requestError
      );

      setMinError(
        requestMessage(
          requestError,
          inventoryText("inventory.MinQuantitySaveFailed")
        )
      );
    } finally {
      setSavingMin(false);
    }
  };

  const openRecords = async (product = null) => {
    setRecordsProduct(product);
    setShowRecords(true);
    setRecords([]);
    setRecordsError("");
    setRecordsLoading(true);

    try {
      const params = {
        username: user.username,
        warehouse_id: warehouse.warehouse_id,
      };

      if (product?.product_id) {
        params.product_id = product.product_id;
      }

      const response = await axios.get(
        `${API_URL}/inventory/warehouses/transfer-records/`,
        {
          headers: getHeaders(),
          params,
        }
      );

      setRecords(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (requestError) {
      console.error(
        "Unable to load transfer records:",
        requestError
      );
      setRecordsError(
        requestMessage(
          requestError,
          "Transfer records could not be loaded."
        )
      );
    } finally {
      setRecordsLoading(false);
    }
  };

  const closeEverything = () => {
    setTransferProduct(null);
    setAdjustmentProduct(null);
    setMinProduct(null);
    setShowRecords(false);
    setNotice("");
    onHide();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={closeEverything}
        className="purchase-modal warehouse-inventory-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <div className="warehouse-inventory-title-row">
            <Modal.Title>
              {warehouse?.warehouse_name || "Warehouse"} Products
            </Modal.Title>
            <button
              type="button"
              className="edit-client"
              onClick={() => openRecords(null)}
            >{inventoryText("inventory.ViewAllRecords")}</button>
          </div>
        </Modal.Header>

        <Modal.Body>
          {notice && (
            <div
              className="warehouse-inventory-notice"
              role="status"
            >
              {notice}
            </div>
          )}

          {loading ? (
            <div
              className="warehouse-inventory-state"
              role="status"
            >
              Loading products…
            </div>
          ) : error ? (
            <div
              className={
                "warehouse-inventory-state "
                + "warehouse-inventory-error"
              }
              role="alert"
            >
              {error}
            </div>
          ) : products.length === 0 ? (
            <div className="warehouse-inventory-state">
              No products are currently in this warehouse.
            </div>
          ) : (
            <div className="warehouse-inventory-table-wrap">
              <table className="warehouse-inventory-table">
                <thead>
                  <tr>
                    <th>{inventoryText("inventory.Product")}</th>
                    <th>{inventoryText("inventory.Quantity")}</th>
                    <th>{inventoryText("inventory.MinQuantity")}</th>
                    <th>{inventoryText("inventory.Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.inventory_id}>
                      <td>{product.name || "—"}</td>
                      <td className="warehouse-inventory-quantity">
                        {Number(
                          product.quantity
                        ).toLocaleString("en-US")}
                      </td>
                      <td className="warehouse-inventory-quantity">
                        {Number(
                          product.min_quantity || 0
                        ).toLocaleString("en-US")}
                      </td>
                      <td>
                        <div className="warehouse-inventory-actions">
                          <button
                            type="button"
                            className="edit-client"
                            onClick={() => openTransfer(product)}
                          >
                            {inventoryText("inventory.Transfer")}
                          </button>
                          <button
                            type="button"
                            className="edit-client"
                            onClick={() => openAdjustment(product)}
                          >{inventoryText("inventory.ChangeQuantity")}</button>
                          <button
                            type="button"
                            className="edit-client"
                            onClick={() => openRecords(product)}
                          >{inventoryText("inventory.Records")}</button>
                          <button
                            type="button"
                            className="edit-client"
                            onClick={() => openMin(product)}
                          >{inventoryText("inventory.Edit")}</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className={
              "purchase-button "
              + "purchase-button-secondary"
            }
            onClick={closeEverything}
          >{inventoryText("inventory.Close")}</button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(transferProduct)}
        onHide={closeTransfer}
        className={
          "purchase-modal "
          + "warehouse-transfer-action-modal"
        }
        centered
      >
        <Modal.Header closeButton={!transferring}>
          <Modal.Title>
            Transfer {transferProduct?.name || "Product"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form
            id="warehouse-product-transfer-form"
            onSubmit={submitTransfer}
          >
            <div className="warehouse-transfer-summary">
              <div>
                <span>From</span>
                <strong>
                  {warehouse?.warehouse_name || "—"}
                </strong>
              </div>
              <div>
                <span>{inventoryText("inventory.Available")}</span>
                <strong>
                  {transferProduct?.quantity || 0}
                </strong>
              </div>
            </div>

            <label className="warehouse-transfer-field">{inventoryText("inventory.TransferTo")}<select
                className="form-select"
                value={toWarehouse}
                onChange={(event) =>
                  setToWarehouse(event.target.value)
                }
                disabled={transferring}
                required
              >
                <option value="">{inventoryText("inventory.SelectWarehouse")}</option>
                {warehouses
                  .filter(
                    (item) =>
                      String(item.warehouse_id)
                      !== String(warehouse?.warehouse_id)
                  )
                  .map((item) => (
                    <option
                      key={item.warehouse_id}
                      value={item.warehouse_id}
                    >
                      {item.warehouse_name}
                    </option>
                  ))}
              </select>
            </label>

            <label className="warehouse-transfer-field">{inventoryText("inventory.Quantity")}<input
                type="number"
                className="form-control"
                min="1"
                max={transferProduct?.quantity || 1}
                step="1"
                value={transferQuantity}
                onChange={(event) =>
                  setTransferQuantity(event.target.value)
                }
                disabled={transferring}
                required
              />
            </label>

            <label className="warehouse-transfer-field">{inventoryText("inventory.Reason")}<textarea
                className="form-control"
                rows="3"
                maxLength="2000"
                value={transferReason}
                onChange={(event) =>
                  setTransferReason(event.target.value)
                }
                disabled={transferring}
                required
              />
            </label>

            {transferError && (
              <div
                className="warehouse-transfer-error"
                role="alert"
              >
                {transferError}
              </div>
            )}
          </form>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className={
              "purchase-button "
              + "purchase-button-secondary"
            }
            onClick={closeTransfer}
            disabled={transferring}
          >{inventoryText("inventory.Cancel")}</button>
          <button
            type="submit"
            form="warehouse-product-transfer-form"
            className="purchase-button"
            disabled={transferring}
          >
            {transferring ? "Transferring…" : "Transfer"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(adjustmentProduct)}
        onHide={closeAdjustment}
        className={
          "purchase-modal "
          + "warehouse-transfer-action-modal"
        }
        centered
      >
        <Modal.Header closeButton={!adjusting}>
          <Modal.Title>
            Change Quantity — {adjustmentProduct?.name || "Product"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form
            id="warehouse-quantity-adjustment-form"
            onSubmit={submitAdjustment}
          >
            <div className="warehouse-transfer-summary">
              <div>
                <span>{inventoryText("inventory.Warehouse")}</span>
                <strong>
                  {warehouse?.warehouse_name || "—"}
                </strong>
              </div>
              <div>
                <span>{inventoryText("inventory.CurrentQuantity")}</span>
                <strong>
                  {adjustmentProduct?.quantity || 0}
                </strong>
              </div>
            </div>

            <label className="warehouse-transfer-field">{inventoryText("inventory.Adjustment")}<select
                className="form-select"
                value={adjustmentOperation}
                onChange={(event) => {
                  setAdjustmentOperation(event.target.value);
                  setAdjustmentError("");
                }}
                disabled={adjusting}
                required
              >
                <option value="increase">{inventoryText("inventory.Increase")}</option>
                <option value="decrease">{inventoryText("inventory.Decrease")}</option>
              </select>
            </label>

            <label className="warehouse-transfer-field">{inventoryText("inventory.Amount")}<input
                type="number"
                className="form-control"
                min="1"
                max={
                  adjustmentOperation === "decrease"
                    ? adjustmentProduct?.quantity || 1
                    : undefined
                }
                step="1"
                value={adjustmentQuantity}
                onChange={(event) =>
                  setAdjustmentQuantity(event.target.value)
                }
                disabled={adjusting}
                required
              />
            </label>

            <label className="warehouse-transfer-field">{inventoryText("inventory.Reason")}<textarea
                className="form-control"
                rows="3"
                maxLength="2000"
                value={adjustmentReason}
                onChange={(event) =>
                  setAdjustmentReason(event.target.value)
                }
                disabled={adjusting}
                required
              />
            </label>

            {adjustmentError && (
              <div
                className="warehouse-transfer-error"
                role="alert"
              >
                {adjustmentError}
              </div>
            )}
          </form>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className={
              "purchase-button "
              + "purchase-button-secondary"
            }
            onClick={closeAdjustment}
            disabled={adjusting}
          >{inventoryText("inventory.Cancel")}</button>
          <button
            type="submit"
            form="warehouse-quantity-adjustment-form"
            className={
              "purchase-button "
              + "purchase-button-primary"
            }
            disabled={adjusting}
          >
            {adjusting ? "Saving…" : "Save"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRecords}
        onHide={() => setShowRecords(false)}
        className={
          "purchase-modal "
          + "warehouse-transfer-records-modal"
        }
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {recordsProduct?.name
              ? `${recordsProduct.name} Records`
              : `${
                warehouse?.warehouse_name || "Warehouse"
              } Records`}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {recordsLoading ? (
            <div
              className="warehouse-inventory-state"
              role="status"
            >
              Loading records…
            </div>
          ) : recordsError ? (
            <div
              className="warehouse-transfer-error"
              role="alert"
            >
              {recordsError}
            </div>
          ) : records.length === 0 ? (
            <div className="warehouse-inventory-state">{inventoryText("inventory.NoTransferRecordsAreAvailable")}</div>
          ) : (
            <div className="warehouse-transfer-records-list">
              {records.map((record) => (
                <article
                  className="warehouse-transfer-record"
                  key={record.record_id}
                >
                  <div className={
                    "warehouse-transfer-record-description"
                  }>
                    {record.description}
                  </div>
                  <div className={
                    "warehouse-transfer-record-reason"
                  }>
                    <strong>Reason:</strong>{" "}
                    {record.reason}
                  </div>
                  <time dateTime={record.creation_date}>
                    {new Date(
                      record.creation_date
                    ).toLocaleString()}
                  </time>
                </article>
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className={
              "purchase-button "
              + "purchase-button-secondary"
            }
            onClick={() => setShowRecords(false)}
          >{inventoryText("inventory.Close")}</button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={Boolean(minProduct)}
        onHide={closeMin}
        className={
          "purchase-modal "
          + "warehouse-transfer-action-modal"
        }
        centered
      >
        <Modal.Header closeButton={!savingMin}>
          <Modal.Title>
            {inventoryText("inventory.SetMinQuantity")} — {minProduct?.name || "Product"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form
            id="warehouse-min-quantity-form"
            onSubmit={submitMin}
          >
            <div className="warehouse-transfer-summary">
              <div>
                <span>{inventoryText("inventory.Warehouse")}</span>
                <strong>
                  {warehouse?.warehouse_name || "—"}
                </strong>
              </div>
              <div>
                <span>{inventoryText("inventory.CurrentQuantity")}</span>
                <strong>
                  {minProduct?.quantity || 0}
                </strong>
              </div>
            </div>

            <label className="warehouse-transfer-field">{inventoryText("inventory.MinQuantity")}<input
                type="number"
                className="form-control"
                min="0"
                step="1"
                value={minQuantity}
                onChange={(event) =>
                  setMinQuantity(event.target.value)
                }
                disabled={savingMin}
                required
              />
            </label>

            {minError && (
              <div
                className="warehouse-transfer-error"
                role="alert"
              >
                {minError}
              </div>
            )}
          </form>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className={
              "purchase-button "
              + "purchase-button-secondary"
            }
            onClick={closeMin}
            disabled={savingMin}
          >{inventoryText("inventory.Cancel")}</button>
          <button
            type="submit"
            form="warehouse-min-quantity-form"
            className="purchase-button"
            disabled={savingMin}
          >
            {savingMin
              ? inventoryText("inventory.Saving")
              : inventoryText("inventory.Save")}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default WarehouseInventoryModal;
